/**
 * DominioParametros — bloco de field-rows de UM domínio do catálogo +
 * previews vivos (§1.9) + tabela read-only do motor empírico (medido).
 *
 * O estado de edição vive no pai (Config) — aqui só render + onChange.
 * A savebar por seção também é do pai (uma só, para o domínio ativo).
 */
import React, { useEffect, useMemo, useState } from "react";

import { useAuth } from "../../../context/AuthContext";
import { coeficientesMedidos } from "../../../services/producaoV2";
import { Card, EmptyMsg, Pill, SectionTitle } from "../../Orcamentos/styles";
import {
  Mono,
  NotaDiscreta,
  TabelaFixa,
  TituloBloco,
  fmtNum,
} from "../_shared/ui";
import FieldRow from "./FieldRow";
import { BarraAmostraFever, FieldAjuda, FieldMeta } from "./ui";

/** Capacidade-minuto derivada AO VIVO dos valores locais do calendário. */
function capacidadeMinutoDerivada(valores) {
  const paraMin = (hhmm) => {
    const [h, m] = String(hhmm || "").split(":").map(Number);
    if (!Number.isFinite(h) || !Number.isFinite(m)) return null;
    return h * 60 + m;
  };
  const inicio = paraMin(valores.horario_inicio);
  const fim = paraMin(valores.horario_fim);
  if (inicio === null || fim === null || fim <= inicio) return null;
  let brutos = fim - inicio;
  for (const intervalo of valores.intervalos || []) {
    if (intervalo?.pago) continue;
    const i = paraMin(intervalo?.inicio);
    const f = paraMin(intervalo?.fim);
    if (i !== null && f !== null && f > i) brutos -= f - i;
  }
  const eficiencia = Number(valores.eficiencia_planejada_pct);
  if (!Number.isFinite(eficiencia) || eficiencia <= 0) return null;
  return Math.max(brutos, 0) * (eficiencia / 100);
}

function TabelaCoeficientesMedidos() {
  const { request } = useAuth();
  const [dados, setDados] = useState(null);
  const [erro, setErro] = useState(null);

  useEffect(() => {
    coeficientesMedidos(request)
      .then(setDados)
      .catch((err) =>
        setErro(err?.message || "Não foi possível carregar os coeficientes."),
      );
  }, [request]);

  return (
    <Card>
      <SectionTitle>Coeficientes medidos pela bipagem</SectionTitle>
      {erro && <EmptyMsg>{erro}</EmptyMsg>}
      {!dados && !erro && <EmptyMsg>Carregando…</EmptyMsg>}
      {dados && (
        <>
          <TabelaFixa>
            <colgroup>
              <col style={{ width: "28%" }} />
              <col style={{ width: "14%" }} />
              <col style={{ width: "18%" }} />
              <col style={{ width: "12%" }} />
              <col style={{ width: "16%" }} />
              <col style={{ width: "12%" }} />
            </colgroup>
            <thead>
              <tr>
                <th>Etapa</th>
                <th>Fase</th>
                <th>Mediana touch (min)</th>
                <th>Amostras</th>
                <th>Confiável</th>
                <th>Prior</th>
              </tr>
            </thead>
            <tbody>
              {(dados.etapas || []).map((etapa) => (
                <tr key={etapa.codigo}>
                  <td title={etapa.label}>{etapa.label}</td>
                  <td>
                    {etapa.fase === "MARCENARIA" ? "F6" : "F5"}
                  </td>
                  <td>
                    <Mono>
                      {etapa.mediana_touch_min === null
                        ? "—"
                        : fmtNum(etapa.mediana_touch_min, 1)}
                    </Mono>
                  </td>
                  <td>
                    <Mono>{etapa.n_amostras}</Mono>
                  </td>
                  <td>
                    {etapa.confiavel ? (
                      <Pill $tone="success">n ≥ {dados.n_minimo_amostras}</Pill>
                    ) : (
                      <Pill $tone="neutral">amostra insuficiente</Pill>
                    )}
                  </td>
                  <td>
                    <Mono>
                      {etapa.prior?.valor !== undefined && etapa.prior !== null
                        ? `${fmtNum(etapa.prior.valor, 1)} ${etapa.prior.driver || ""}`
                        : "–"}
                    </Mono>
                  </td>
                </tr>
              ))}
            </tbody>
          </TabelaFixa>
          <NotaDiscreta>
            O coeficiente medido substitui o prior quando n ≥{" "}
            {dados.n_minimo_amostras}. Sem amostra suficiente, a mediana fica
            "—" — nunca número inventado.
          </NotaDiscreta>
        </>
      )}
    </Card>
  );
}

export default function DominioParametros({
  dominio,
  entradas,
  valores, // {chave: valor efetivo (edição local ?? persistido)}
  editadas, // Set de chaves com edição não-salva
  errosPorChave,
  disabled,
  highlightChave,
  onChange,
  onRestaurar,
}) {
  const previewCapacidade = useMemo(
    () => (dominio === "calendario" ? capacidadeMinutoDerivada(valores) : null),
    [dominio, valores],
  );

  return (
    <>
      <Card>
        {entradas.map((entrada) => (
          <FieldRow
            key={entrada.chave}
            entrada={entrada}
            valor={valores[entrada.chave]}
            editado={editadas.has(entrada.chave)}
            erro={errosPorChave?.[entrada.chave]}
            disabled={disabled}
            encontrado={highlightChave === entrada.chave}
            onChange={(v) => onChange(entrada.chave, v)}
            onRestaurar={() => onRestaurar(entrada)}
          />
        ))}

        {dominio === "calendario" && (
          <div style={{ padding: "14px 4px 4px" }}>
            <TituloBloco>Capacidade-minuto derivada (preview)</TituloBloco>
            <FieldMeta style={{ marginBottom: 6 }}>
              {previewCapacidade === null ? (
                <span>— horários/eficiência incompletos</span>
              ) : (
                <span>
                  Fórmula: (fim − início − intervalos não pagos) × eficiência ={" "}
                  <strong>{fmtNum(previewCapacidade, 1)} min úteis/dia</strong>{" "}
                  (~{fmtNum(previewCapacidade / 60, 1)}h)
                </span>
              )}
            </FieldMeta>
            <FieldAjuda>
              Read-only — recalculada ao vivo conforme você edita os campos
              acima. É a base da capacidade do board da Marcenaria.
            </FieldAjuda>
          </div>
        )}

        {dominio === "toc" && (
          <div style={{ padding: "14px 4px 4px" }}>
            <TituloBloco>Fever chart (preview dos limiares)</TituloBloco>
            <BarraAmostraFever
              $amarelo={Number(valores.buffer_limiar_amarelo_pct) || 0}
              $vermelho={Number(valores.buffer_limiar_vermelho_pct) || 0}
              aria-hidden="true"
            >
              <span className="verde" />
              <span className="amarelo" />
              <span className="vermelho" />
            </BarraAmostraFever>
            <FieldAjuda style={{ marginTop: 6 }}>
              A barra repinta ao vivo conforme os % digitados — verde até{" "}
              {fmtNum(Number(valores.buffer_limiar_amarelo_pct) || 0, 0)}%,
              amarelo até{" "}
              {fmtNum(Number(valores.buffer_limiar_vermelho_pct) || 0, 0)}%,
              vermelho acima.
            </FieldAjuda>
          </div>
        )}
      </Card>

      {dominio === "motor_empirico" && <TabelaCoeficientesMedidos />}
    </>
  );
}
